/**
 * Workspace Images
 *
 * Uploads and resolves workspace display images.
 *
 * Primary responsibilities:
 * - Store workspace image files in Supabase Storage
 * - Resolve signed image URLs for workspace display
 *
 * Dependencies:
 * - Supabase browser client
 */

import { Tables } from '@/supabase/types'

import { getWorkspaceImageFromStorage as getResilientWorkspaceImage } from '@/lib/supabase/signed-url-manager'
import { supabase } from '@/lib/supabase/browser-client'

// --------------

export const uploadWorkspaceImage = async (
  workspace: Tables<'workspaces'>,
  image: File,
  uploaderUserId = workspace.user_id
) => {
  const bucket = 'workspace_images'

  const imageSizeLimit = 6000000 // 6MB

  if (image.size > imageSizeLimit) {
    throw new Error(`Image must be less than ${imageSizeLimit / 1000000}MB`)
  }

  const currentPath = workspace.image_path
  const storageOwnerId = uploaderUserId.trim() || workspace.user_id
  const filePath = `${storageOwnerId}/${workspace.id}/${Date.now()}`

  if (currentPath.length > 0 && currentPath.startsWith(`${storageOwnerId}/`)) {
    const { error: deleteError } = await supabase.storage.from(bucket).remove([currentPath])

    if (deleteError) {
      throw new Error('Error deleting old image')
    }
  }

  const { error } = await supabase.storage.from(bucket).upload(filePath, image, {
    upsert: true
  })

  if (error) {
    throw new Error('Error uploading image')
  }

  return filePath
}

// --------------

export const getWorkspaceImageFromStorage = getResilientWorkspaceImage
